import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useMagnum } from '@/context/MagnumContext'
import { apiFetch } from '@/lib/apiFetch'
import { wordSizeClass } from '@/lib/utils'
import { useExerciseSession } from '@/hooks/useExerciseSession'
import { fetchMagnumMoment, EffectData } from '@/lib/magnumMoment'
import EffectRenderer from '@/components/EffectRenderer'

type GameState = 'idle' | 'loading' | 'playing' | 'result' | 'done'

interface WordChoice {
  id: number
  text: string
  gradeLevel: number
}

interface SynonymQuestion {
  mainWord: WordChoice
  choices: WordChoice[]
  correctWordId: number
}

const STREAK_MILESTONES = [3, 5, 7, 10]
const STORAGE_KEY = 'exercise_progress_SYNONYM_MATCH'

export default function SynonymMatchPage() {
  const { user } = useAuth()
  const magnum = useMagnum()
  const { sessionId, stats, activeMs, isIdle, startSession, endSession, recordAttempt, dismissIdle, restoreSession, persist, clearPersisted } = useExerciseSession({ exerciseType: 'SYNONYM_MATCH' })

  const [state, setState] = useState<GameState>('idle')
  const [questions, setQuestions] = useState<SynonymQuestion[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [chosenId, setChosenId] = useState<number | null>(null)
  const [feedback, setFeedback] = useState<{ correct: boolean } | null>(null)
  const [activeEffects, setActiveEffects] = useState<EffectData[] | null>(null)
  const [showNewRecord, setShowNewRecord] = useState(false)
  const questionStartRef = useRef(Date.now())

  const currentQuestion = questions[currentIndex] || null

  useEffect(() => {
    magnum.setFrameIdle(isIdle)
    if (isIdle) {
      fetchMagnumMoment({ contextType: 'IDLE_RETURN', exerciseType: 'SYNONYM_MATCH',
      category: 'LANGUAGE', streak: stats.streak, sessionId, activeMs })
        .then((m) => magnum.handleEngineResponse(m))
        .catch(() => magnum.say("Still here. Still waiting. Still adorable."))
    }
  }, [isIdle])

  const persistProgress = (qs: SynonymQuestion[], idx: number) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ questions: qs, currentIndex: idx }))
    } catch {}
    persist()
  }

  const startGame = async () => {
    setState('loading')
    try {
      const [questionData] = await Promise.all([
        apiFetch('/exercises/synonym-match?count=10'),
        startSession(),
      ])
      setQuestions(questionData)
      setCurrentIndex(0)
      setChosenId(null)
      setFeedback(null)
      setState('playing')
      questionStartRef.current = Date.now()
      persistProgress(questionData, 0)

      fetchMagnumMoment({ contextType: 'GAME_STARTED', exerciseType: 'SYNONYM_MATCH',
      category: 'LANGUAGE', streak: 0, sessionId })
        .then((m) => magnum.handleEngineResponse(m, 'happy'))
        .catch(() => {})
    } catch {
      setState('idle')
    }
  }

  const handleChoice = async (choice: WordChoice) => {
    if (!currentQuestion || state !== 'playing') return

    const responseMs = Date.now() - questionStartRef.current
    const correct = choice.id === currentQuestion.correctWordId

    const newStreak = correct ? stats.streak + 1 : 0
    const isMilestone = correct && STREAK_MILESTONES.includes(newStreak)
    const contextType = correct
      ? (isMilestone ? 'STREAK_MILESTONE' : 'CORRECT_ANSWER')
      : 'WRONG_ANSWER'

    setChosenId(choice.id)
    setFeedback({ correct })
    setState('result')

    fetchMagnumMoment({
      contextType,
      exerciseType: 'SYNONYM_MATCH',
      category: 'LANGUAGE',
      wordId: currentQuestion.mainWord.id,
      wordText: currentQuestion.mainWord.text,
      responseGiven: choice.text,
      correct,
      responseMs,
      activeMs,
      streak: newStreak,
      sessionId,
    }).then((moment) => {
      magnum.handleEngineResponse(moment, correct ? 'happy' : 'encouraging')
      if (moment.effects?.length) setActiveEffects(moment.effects)
      if (moment.timing?.newRecords?.length) {
        setShowNewRecord(true)
        setTimeout(() => setShowNewRecord(false), 3000)
      }
    }).catch(() => {})

    await recordAttempt(currentQuestion.mainWord.id, correct, responseMs, {
      mainWordId: currentQuestion.mainWord.id,
      chosenWordId: choice.id,
      correctWordId: currentQuestion.correctWordId,
      distractorWordIds: currentQuestion.choices
        .filter((c) => c.id !== currentQuestion.correctWordId)
        .map((c) => c.id),
    })
  }

  const nextQuestion = () => {
    const next = currentIndex + 1
    if (next >= questions.length) {
      setState('done')
      clearPersisted()
      try { sessionStorage.removeItem(STORAGE_KEY) } catch {}

      fetchMagnumMoment({ contextType: 'SESSION_END', exerciseType: 'SYNONYM_MATCH',
      category: 'LANGUAGE', streak: stats.streak, sessionId, activeMs })
        .then((m) => magnum.handleEngineResponse(m, 'excited'))
        .catch(() => {})

      endSession()
    } else {
      setCurrentIndex(next)
      setChosenId(null)
      setFeedback(null)
      setState('playing')
      questionStartRef.current = Date.now()
      persistProgress(questions, next)
    }
  }

  useEffect(() => {
    if (state === 'result') {
      const timer = setTimeout(nextQuestion, 1500)
      return () => clearTimeout(timer)
    }
  }, [state, currentIndex])

  useEffect(() => {
    if (state === 'idle') {
      const restored = restoreSession()
      if (restored) {
        try {
          const raw = sessionStorage.getItem(STORAGE_KEY)
          if (raw) {
            const data = JSON.parse(raw)
            if (data.questions?.length > 0) {
              setQuestions(data.questions)
              setCurrentIndex(data.currentIndex || 0)
              setState('playing')
              questionStartRef.current = Date.now()
              return
            }
          }
        } catch {}
      }
      startGame()
    }
  }, [])

  useEffect(() => {
    if (isIdle && state === 'result') dismissIdle()
  }, [state])

  if (!user || user.type !== 'child') return null

  if (state === 'loading') {
    return (
      <div className="py-16 text-center">
        <p className="text-lg text-muted-foreground">Loading questions...</p>
      </div>
    )
  }

  if (state === 'done') {
    const accuracy = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0
    return (
      <div className="py-8 text-center space-y-8">
        <div className="text-6xl">🎉</div>
        <h1 className="text-3xl font-bold">Session Complete!</h1>
        <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto">
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold text-primary">{stats.correct}</p>
            <p className="text-xs text-muted-foreground">Correct</p>
          </div>
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </div>
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold text-primary">{accuracy}%</p>
            <p className="text-xs text-muted-foreground">Accuracy</p>
          </div>
        </div>
        <div className="flex gap-4 justify-center pt-4">
          <button
            onClick={startGame}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition"
          >
            Play Again
          </button>
          <Link
            to="/practice"
            className="px-6 py-3 border border-border rounded-xl font-bold hover:bg-accent transition inline-flex items-center"
          >
            Back to Exercises
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="text-center space-y-6">
      <EffectRenderer effects={activeEffects} onDone={() => setActiveEffects(null)} />

      {/* Top bar */}
      <div className="flex items-center justify-between">
        <Link
          to="/practice"
          className="text-sm text-muted-foreground hover:text-foreground transition"
        >
          &larr; Back
        </Link>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>{currentIndex + 1} of {questions.length}</span>
          <span className="text-primary font-medium">{stats.correct} correct</span>
          {stats.total > 0 && (
            <span>Avg: {(stats.avgResponseMs / 1000).toFixed(1)}s</span>
          )}
          {stats.streak >= 3 && (
            <span className="text-amber-500 font-medium">🔥 {stats.streak}</span>
          )}
        </div>
      </div>

      {showNewRecord && (
        <div className="text-center animate-bounce">
          <span className="inline-block px-4 py-2 bg-amber-100 border-2 border-amber-400 rounded-xl text-amber-800 font-bold text-sm">
            New Record!
          </span>
        </div>
      )}

      {/* Progress bar */}
      <div className="w-full bg-accent rounded-full h-2">
        <div
          className="bg-primary h-2 rounded-full transition-all duration-300"
          style={{ width: `${((currentIndex + (state === 'result' ? 1 : 0)) / questions.length) * 100}%` }}
        />
      </div>

      {/* Main word */}
      {currentQuestion && (
        <>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-2">Which word means the same as...</p>
            <p className={`${wordSizeClass(currentQuestion.mainWord.text, 'match')} font-black tracking-tight break-words`}>
              {currentQuestion.mainWord.text}
            </p>
          </div>

          {/* Choices */}
          <div className="space-y-3 max-w-md mx-auto">
            {currentQuestion.choices.map((choice) => {
              let btnClass = 'w-full px-6 py-4 rounded-xl text-lg font-bold transition border-2 '
              if (state === 'result') {
                if (choice.id === currentQuestion.correctWordId) {
                  btnClass += 'bg-green-100 border-green-500 text-green-800'
                } else if (choice.id === chosenId) {
                  btnClass += 'bg-red-100 border-red-500 text-red-800'
                } else {
                  btnClass += 'border-border text-muted-foreground opacity-50'
                }
              } else {
                btnClass += 'border-border hover:border-primary hover:bg-accent'
              }

              return (
                <button
                  key={choice.id}
                  onClick={() => handleChoice(choice)}
                  disabled={state === 'result'}
                  className={btnClass}
                >
                  {choice.text}
                </button>
              )
            })}
          </div>

          {/* Result indicator */}
          {state === 'result' && feedback && (
            <div className={`text-lg font-semibold ${feedback.correct ? 'text-primary' : 'text-amber-500'}`}>
              {feedback.correct ? '✓ Correct!' : '✗ Not quite'}
            </div>
          )}
        </>
      )}
    </div>
  )
}
